// plugins/draw/DrawPlugin.ts

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type maplibregl from "maplibre-gl";
import type { RootState } from "../../store/store";
import { useMapContext } from "../../core/MapContext";
import { createDrawingTool } from "./aoiToolFactory";
import { syncPointMarkers } from "./helpers/maplibre";
import { DrawAOIFeature } from "./types";
import { AOI_SOURCE_ID, buildDrawGeoJSON, EDIT_AOI_SOURCE_ID } from "./consts";
import {
  checkAOIArea,
  getAOIMetrics,
  type AOIAreaLimits,
} from "../../../ExploreTasking/ultis";
import {
  AreaLabelMarkers,
  topOfPolygon,
  type AreaLabel,
} from "./helpers/areaLabelMarkers";

interface AOIPluginProps
{
  allowMoveActive?: boolean;
  /** Provider's accepted area range, km². Omitted means nothing is checked. */
  aoiAreaLimits?: AOIAreaLimits;
}
export const AOIPluginComponent = ({
  allowMoveActive = true,
  aoiAreaLimits,
}: AOIPluginProps = {}) =>
{
  const { t } = useTranslation();
  const { mode, features, editingFeatureId, featureToDelete, activeFeatureId } = useSelector(
    (state: RootState) => state.aoi,
  );
  let featureVisible = features?.filter((f: DrawAOIFeature) => f.visible);

  const {
    basemaps,
    selectedBasemap,
    view: mapView,
    mousePosition,
  } = useSelector((state: RootState) => state.map);

  const { layers } = useSelector((state: RootState) => state.layer);

  const dispatch = useDispatch();

  const { mapType, mapInstance } = useMapContext();

  /**
   * The area caption for every drawn polygon, plus which ones the provider
   * would refuse.
   *
   * Every polygon is labelled, not only the offending ones: the figure in the
   * side panel describes one AOI, so once there are two shapes on the map
   * nothing connects a number to a shape.
   *
   * Recomputed from the features rather than stored on them — the limit belongs
   * to the provider, so the same polygon can go from acceptable to not simply by
   * switching provider, with the shape untouched.
   */
  const areaLabels = useMemo(() =>
  {
    const ids = new Set<string>();
    const labels: AreaLabel[] = [];

    (features ?? []).forEach((feature: DrawAOIFeature) =>
    {
      if (feature.geometry?.type !== "Polygon") return;

      const metrics = getAOIMetrics(feature);
      if (!metrics) return;

      const areaKm2 = metrics.area / 1_000_000;
      const check = checkAOIArea(feature, aoiAreaLimits ?? null);
      const invalid = Boolean(check && check.status !== "ok");

      if (invalid) ids.add(feature.id);

      const at = topOfPolygon(feature);
      if (!at || feature.visible === false) return;

      const area = areaKm2.toLocaleString(undefined, {
        maximumFractionDigits: areaKm2 < 10 ? 2 : 0,
      });

      labels.push({
        featureId: feature.id,
        at,
        invalid,
        text:
          check?.status === "too-large"
            ? t("Area {{area}} km² exceeds the {{max}} km² limit", {
              area,
              max: aoiAreaLimits?.maxKm2,
            })
            : check?.status === "too-small"
              ? t("Area {{area}} km² is below the {{min}} km² minimum", {
                area,
                min: aoiAreaLimits?.minKm2,
              })
              : t("Area {{area}} km²", { area }),
      });
    });

    return { ids, labels };
  }, [features, aoiAreaLimits, t]);

  useEffect(() =>
  {
    if (!mapInstance) return;

    const source = mapInstance.getSource(AOI_SOURCE_ID);
    if (source)
    {
      source.setData(buildDrawGeoJSON(features, activeFeatureId, areaLabels.ids));
    }

    // Every reducer that moves activeFeatureId also replaces `features`, so
    // [features] alone would already fire — it is listed so a future
    // selection-only reducer cannot silently strand the solid/dashed styling.
  }, [features, activeFeatureId, areaLabels]);

  // The caption beside each shape. DOM markers, so they need no style to be
  // loaded and survive a basemap change on their own.
  const areaMarkersRef = useRef<AreaLabelMarkers | null>(null);

  useEffect(() =>
  {
    if (mapType !== "maplibre" || !mapInstance) return;

    if (!areaMarkersRef.current)
    {
      areaMarkersRef.current = new AreaLabelMarkers(mapInstance);
    }
    areaMarkersRef.current.setData(areaLabels.labels);
  }, [areaLabels, mapInstance, mapType]);

  useEffect(
    () => () =>
    {
      areaMarkersRef.current?.destroy();
      areaMarkersRef.current = null;
    },
    [],
  );

  // Confirmed Point AOI features render as real maplibregl.Marker pins rather
  // than the small draw-point circle (which stays reserved for the transient
  // mouse-position dot shown while drawing a Polygon/Rectangle).
  const pointMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());

  useEffect(() =>
  {
    if (mapType !== "maplibre") return;

    syncPointMarkers(mapInstance, features, pointMarkersRef.current);

    return () =>
    {
      pointMarkersRef.current.forEach((marker) => marker.remove());
      pointMarkersRef.current.clear();
    };
  }, [features, mapInstance, mapType]);

  useEffect(() =>
  {
    if (!mapInstance) return;

    // if (mode === "NONE") return;

    const tool = createDrawingTool(
      mapInstance,
      mapType,
      mode,
      editingFeatureId,
      featureVisible,
      dispatch,
      layers,
      activeFeatureId,
      allowMoveActive,
      // Always supplied, even with no limits to enforce: the running area is
      // worth seeing while dragging whether or not a provider caps it, and it
      // matches the caption the finished shape keeps.
      {
        limits: aoiAreaLimits,
        format: (areaKm2, status) =>
        {
          const area = areaKm2.toLocaleString(undefined, {
            maximumFractionDigits: areaKm2 < 10 ? 2 : 0,
          });

          if (status === "too-large")
          {
            return t("Area {{area}} km² exceeds the {{max}} km² limit", {
              area,
              max: aoiAreaLimits?.maxKm2,
            });
          }
          if (status === "too-small")
          {
            return t("Area {{area}} km² is below the {{min}} km² minimum", {
              area,
              min: aoiAreaLimits?.minKm2,
            });
          }
          return t("Area {{area}} km²", { area });
        },
      },
    );
    tool.activate();

    return () =>
    {
      tool.deactivate();
    };
    // activeFeatureId belongs here: the tool captures it once into its own state
    // and never refreshes it. Without this, selecting a different AOI leaves the
    // tool stamping the OLD id, so the next write through drawGJ/pushDraw (finish
    // a draw, drag a rectangle, switch basemap) flips the solid/dashed styling
    // onto the wrong polygon — and gates move-active on a stale id too.
    // aoiAreaLimits is in here because the tool captures the guard once: a
    // provider change has to rebuild it or the live preview keeps enforcing the
    // previous provider's range.
  }, [mode, mapInstance, editingFeatureId, featureToDelete, selectedBasemap, activeFeatureId, allowMoveActive, aoiAreaLimits, t]);

  return null;
};
